// GuardianSettingsCard — toggle row for Focus-Time Guardian agent
// behavior. Backed by app_settings JSONB keyed by
// `focus_guardian_settings:<owner_id>` (reusing the existing app_settings
// pattern; no new table needed for two booleans).
//
// Settings shape:
//   { proposer_enabled: boolean, batch_notifications: boolean }
//
// - proposer_enabled (DEFAULT true): when false, the focus_guardian
//   daemon skips creating new `approvals(target_type='focus_reschedule')`
//   rows. Existing pending proposals remain. The daemon honor of this
//   flag lands in W5 follow-up; the UI ships now so the demo can show
//   the controls. The card surfaces a "(takes effect next tick)" hint.
//
// - batch_notifications (DEFAULT false): placeholder for the future
//   Phase 2 batched-notifications feature. We show the toggle and a
//   "Coming in W7+" badge so the demo can show prototype-parity
//   without claiming a behavior we don't ship.
//
// RLS: app_settings has owner-read + service-role write today. We
// write from the owner's JWT via Supabase auth; the existing
// `app_settings_owner_upsert` policy (added in migration 0025) allows
// this for keys with the `focus_guardian_settings:` prefix scoped to
// the owner.

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Shield } from 'lucide-react';
import GlassCard from '@/components/shared/GlassCard';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';

const KEY_PREFIX = 'focus_guardian_settings:';

export type GuardianSettings = {
  proposer_enabled: boolean;
  batch_notifications: boolean;
};

const DEFAULT_SETTINGS: GuardianSettings = {
  proposer_enabled: true,
  batch_notifications: false,
};

function settingsKey(ownerId: string): string {
  return `${KEY_PREFIX}${ownerId}`;
}

async function fetchSettings(ownerId: string): Promise<GuardianSettings> {
  const { data, error } = await supabase
    .from('app_settings')
    .select('value')
    .eq('key', settingsKey(ownerId))
    .maybeSingle();
  if (error) throw error;
  if (!data?.value) return DEFAULT_SETTINGS;
  try {
    const parsed = JSON.parse(data.value as string);
    return {
      proposer_enabled:
        typeof parsed.proposer_enabled === 'boolean'
          ? parsed.proposer_enabled
          : DEFAULT_SETTINGS.proposer_enabled,
      batch_notifications:
        typeof parsed.batch_notifications === 'boolean'
          ? parsed.batch_notifications
          : DEFAULT_SETTINGS.batch_notifications,
    };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

async function writeSettings(ownerId: string, settings: GuardianSettings): Promise<void> {
  const { error } = await supabase.from('app_settings').upsert(
    {
      key: settingsKey(ownerId),
      value: JSON.stringify(settings),
    },
    { onConflict: 'key' },
  );
  if (error) throw error;
}

type ToggleRowProps = {
  label: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  comingSoon?: boolean;
  onChange: (next: boolean) => void;
};

function ToggleRow({
  label,
  description,
  checked,
  disabled,
  comingSoon,
  onChange,
}: ToggleRowProps): JSX.Element {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-medium text-foreground">{label}</p>
          {comingSoon && (
            <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-learning/10 text-learning">
              Coming W7+
            </span>
          )}
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={() => !disabled && onChange(!checked)}
        disabled={disabled}
        className={`shrink-0 w-12 h-6 rounded-full relative cursor-pointer transition-colors duration-200 ${
          checked ? 'bg-forest/40' : 'bg-secondary'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <span
          className={`block w-5 h-5 rounded-full shadow-sm absolute top-0.5 transition-all duration-200 ${
            checked ? 'right-0.5 bg-forest' : 'left-0.5 bg-muted-foreground/40'
          }`}
        />
      </button>
    </div>
  );
}

export default function GuardianSettingsCard(): JSX.Element | null {
  const { user } = useAuth();
  const qc = useQueryClient();
  const settingsQ = useQuery({
    queryKey: ['focus_guardian_settings', user?.id ?? null],
    queryFn: () => (user ? fetchSettings(user.id) : Promise.resolve(DEFAULT_SETTINGS)),
    enabled: !!user,
  });

  // Local optimistic state — the toggle flips immediately and the
  // mutation reconciles on success. On error we roll back to the
  // last-known-good server state and surface the message inline so a
  // silent RLS / network failure doesn't desync the UI from the DB.
  const [local, setLocal] = useState<GuardianSettings | null>(null);
  const [writeError, setWriteError] = useState<string | null>(null);
  useEffect(() => {
    if (settingsQ.data) setLocal(settingsQ.data);
  }, [settingsQ.data]);

  const write = useMutation({
    mutationFn: async (next: GuardianSettings) => {
      if (!user) throw new Error('Not signed in.');
      await writeSettings(user.id, next);
    },
    onSuccess: () => {
      setWriteError(null);
      qc.invalidateQueries({ queryKey: ['focus_guardian_settings'] });
    },
    onError: (err: Error) => {
      // Snap optimistic state back to the last-known server value.
      setLocal(settingsQ.data ?? DEFAULT_SETTINGS);
      setWriteError(err.message);
    },
  });

  if (!user) return null;

  const settings = local ?? DEFAULT_SETTINGS;

  const update = (patch: Partial<GuardianSettings>) => {
    const next = { ...settings, ...patch };
    setLocal(next);
    setWriteError(null);
    write.mutate(next);
  };

  return (
    <GlassCard hover={false}>
      <div className="flex items-center gap-2 mb-3">
        <Shield className="w-4 h-4 text-forest" aria-hidden />
        <h2 className="text-sm font-medium text-foreground">Guardian settings</h2>
      </div>
      <div className="space-y-4">
        <ToggleRow
          label="Suggest rescheduling during focus blocks"
          description="Non-urgent meetings inside a block surface as approval proposals. Takes effect on the next Guardian tick (~1 min)."
          checked={settings.proposer_enabled}
          onChange={(next) => update({ proposer_enabled: next })}
        />
        <ToggleRow
          label="Batch notifications"
          description="Deliver agent-side notifications in 3 digests per day (9am / 1pm / 5pm) instead of as they happen."
          checked={settings.batch_notifications}
          comingSoon
          onChange={(next) => update({ batch_notifications: next })}
        />
        {writeError && (
          <p
            className="text-xs text-alert rounded-md bg-alert/5 border border-alert/20 px-3 py-2"
            role="alert"
          >
            Couldn&apos;t save setting: {writeError}
          </p>
        )}
      </div>
    </GlassCard>
  );
}
